import { useTranslation } from "react-i18next";
import { Bookmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CategoryIcon } from "@/components/icons";
import { cn } from "@/lib/utils";
import { ManualStateBadge } from "./ManualStateBadge.js";
import { ManualLockBadge } from "./ManualLockBadge.js";
import { ManualKebabMenu } from "./ManualKebabMenu.js";
import type { ManualCardProps } from "./ManualCard.js";

/**
 * One manual as a LIST row (mockup #14's list view — the grid's horizontal counterpart). Same content + actions as
 * {@link ManualCard}, laid out in a single line: category · name (the drawer trigger) · AR tag · lifecycle badge ·
 * favourite · kebab. Shares {@link ManualCardProps} so the manuals view can swap grid ⇄ list with identical wiring.
 */
export function ManualListRow({
  manual,
  isFavorite,
  onToggleFavorite,
  archived,
  canManage,
  canConfirm,
  onOpenDetails,
  onDuplicate,
  onMove,
  onArchive,
  onUnarchive,
  onDelete,
  onTransition,
}: ManualCardProps) {
  const { t } = useTranslation();
  return (
    // `relative`: the name button's `after:` overlay spans the whole row, so a click anywhere opens the details drawer
    // (owner request); the badges + star/kebab sit above it (`relative z-10`) and keep their own behaviour.
    <div className="border-border hover:bg-muted/40 relative flex cursor-pointer items-center gap-3 border-b px-3 py-3.5 transition-colors">
      <span className="text-muted-foreground hidden w-40 shrink-0 items-center gap-1 text-xs sm:inline-flex">
        <CategoryIcon aria-hidden="true" className="size-3.5 shrink-0" />
        <span className="truncate">{manual.categoryName}</span>
      </span>
      <h3 className="min-w-0 flex-1 text-sm font-medium">
        <button
          type="button"
          onClick={() => onOpenDetails(manual)}
          className="focus-visible:ring-ring/50 block truncate rounded-sm text-left outline-none after:absolute after:inset-0 after:content-[''] hover:underline focus-visible:ring-2"
        >
          {manual.name}
        </button>
      </h3>
      {manual.isArManual ? (
        <span className="bg-muted text-muted-foreground shrink-0 rounded px-1.5 py-0.5 text-[0.65rem] font-medium">
          {t("manualManagement.badge.ar")}
        </span>
      ) : null}
      <span className="relative z-10 inline-flex shrink-0 items-center gap-3">
        <ManualStateBadge state={manual.state} comment={manual.markedComment} />
        {manual.locked ? <ManualLockBadge /> : null}
      </span>
      <div className="relative z-10 flex shrink-0 items-center gap-0.5">
        {!archived ? (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t(isFavorite ? "manualManagement.card.unfavourite" : "manualManagement.card.favourite", { name: manual.name })}
            aria-pressed={isFavorite}
            onPress={() => onToggleFavorite(manual.id)}
          >
            <Bookmark className={cn("size-4", isFavorite ? "fill-attention text-attention" : "text-muted-foreground")} />
          </Button>
        ) : null}
        <ManualKebabMenu
          manual={manual}
          state={manual.state}
          archived={archived}
          canManage={canManage}
          canConfirm={canConfirm}
          onDuplicate={() => onDuplicate(manual)}
          onMove={() => onMove(manual)}
          onArchive={() => onArchive(manual)}
          onUnarchive={() => onUnarchive(manual)}
          onDelete={() => onDelete(manual)}
          onTransition={onTransition ? (key) => onTransition(manual, key) : undefined}
        />
      </div>
    </div>
  );
}
